import * as React from 'react';
import { compose } from 'recompose';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField, WithStyles, withStyles,
} from '@material-ui/core';
import { sortedUniq } from 'lodash';
import { Field, Form, Formik, FormikProps, FormikValues } from 'formik';
import * as yup from 'yup';

import translate from '@/app/utils/translate';
import { CollaborationTemplateAutomationTriggerCondition, ICollaborationTemplate, OptionType } from '../types';
import { useContext, useMemo } from 'react';
import { findSubUnits } from '@/old/utils/helper';
import { FormikCheckboxField, FormikSelectField } from '@/app/components/Formik2';
import Context from '@/app/components/Worklist/context/worklistContext';
import { Add } from '@material-ui/icons';

const styles = {
  fieldRow: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px',
  },
  paper: {
    marginTop: '16px',
    padding: '8px 16px',
  }
};

type InitialFormData = ICollaborationTemplate | Pick<ICollaborationTemplate, Exclude<keyof ICollaborationTemplate, 'id' | 'active'>>;

type OwnProps = {
  open: boolean;
  taskTypes: any[];
  languages: any[];
  defaultLanguage: any;
  onSave: (values: ICollaborationTemplate) => void;
  onClose: () => void;

  initialValues: InitialFormData;
};
type InnerProps = WithStyles<typeof styles>;
type Props = OwnProps & InnerProps;

const WorklistEditDialog = (props: Props) => {
  const { initialValues, open, taskTypes, languages, defaultLanguage, onSave, onClose, classes } = props;

  const { state: { organization: { orgTree: unitOrgTree, allUnitOptions } } } = useContext(Context);

  const yupOptionType = yup.object().shape<OptionType>({
    value: yup.lazy((value) =>
        typeof value === 'number'
            ? yup.number().required()
            : yup.string().required()
    ),
    label: yup.string(),
    isFixed: yup.boolean().notRequired(),
  }).required();

  const validationSchema = yup.object<ICollaborationTemplate>().shape({
    title: yup.string().required(translate.t('laErrorFieldRequir')).max(80, translate.t('laErrorStringMaxChar')),
    description: yup.string().max(3000, translate.t('laErrorTextMaxChar')),
    language: yup.string().required(translate.t('laErrorFieldRequir')),
    type: yup.string().required(translate.t('laErrorFieldRequir')),
    automationEnabled: yup.boolean().defined(),
    automationUnits: yup.array(yupOptionType),
    automationTriggerCondition: yup.string().oneOf(
        Object.values(CollaborationTemplateAutomationTriggerCondition)
    ).when('automationEnabled', { is: true, then: yup.string().required() }),
    automationTriggerOffset: yup.number().when('automationEnabled', { is: true, then: yup.number().required() }),
  });

  const handleSave = (values: ICollaborationTemplate, actions: FormikProps<ICollaborationTemplate>) => {
    onSave(values);
    actions.resetForm();
    onClose();
  };

  const options = useMemo(() => {
    const triggerFieldOptions = [
      {
        value: CollaborationTemplateAutomationTriggerCondition.EMPLOYMENT_START_DATE,
        label: translate.t('fEmploymentStartDateInput'),
      },
      {
        value: CollaborationTemplateAutomationTriggerCondition.EMPLOYMENT_END_DATE,
        label: translate.t('fEmploymentEndDateInput'),
      },
    ];

    return {
      triggerFieldOptions,
    };
  }, []);

  if (!open || !initialValues) {
    return <></>;
  }

  const renderDialog = ({
    errors,
    isSubmitting,
    isValid,
    handleBlur,
    handleChange,
    handleSubmit,
    resetForm,
    touched,
    values,
    setFieldValue,
    setFieldTouched,
  }: FormikProps<ICollaborationTemplate>) => {
    const isUsed = values.isUsed;

    const handleCancel = () => {
      resetForm();
      onClose();
    };

    const handleSubmitClick = () => {
      handleSubmit();
    };

    const submitContent = !isSubmitting ? translate.t('laSave') : (
      <CircularProgress size={22} />
    );

    const setOptionTypesNotFixed = (optionTypes: OptionType[]) => {
      return optionTypes.map((option) => ({ ...option, isFixed: false }));
    };

    const getMatchingSubUnits = (
        unitAudience?: OptionType[],
        includeSubUnits = false,
    ) => {
      if (includeSubUnits || !unitAudience) {
        return findSubUnits(
            unitAudience,
            allUnitOptions,
            unitOrgTree,
            includeSubUnits,
        );
      }

      return setOptionTypesNotFixed(unitAudience);
    };

    const isOptionTypesArrayMatch = (optionTypes1?: OptionType[], optionTypes2?: OptionType[]) => {
      const getComparisonValue = (units?: OptionType[]) => JSON.stringify(units ? sortedUniq(units.map(u => u.value)) : []);

      return getComparisonValue(optionTypes1) === getComparisonValue(optionTypes2);
    };
    const isAllSubUnitsIncluded = isOptionTypesArrayMatch(values.automationUnits, getMatchingSubUnits(values.automationUnits, true));

    const handleIncludeAllSubUnits = () => {
      const updatedValue = setOptionTypesNotFixed(getMatchingSubUnits(values.automationUnits, true));
      setFieldTouched('automationUnits');
      setFieldValue('automationUnits', updatedValue, true);
    };

    const handleAutomationUnitChange = (
        formikBag: FormikProps<FormikValues>,
        v: OptionType[],
    ) => {
      formikBag.setFieldValue('automationUnits', getMatchingSubUnits(v));
    };

    return (
      <Dialog
        open={open}
        onClose={!isSubmitting ? onClose : undefined}
        maxWidth="sm"
        fullWidth
        aria-labelledby="worklist-edit-dialog-title"
      >
        <DialogTitle id="worklist-edit-dialog-title">
          {(!values.id) ? translate.t('title_add_collab_template') : translate.t('title_edit_worklist')}
        </DialogTitle>
        <DialogContent>
          <Form>
            <div className={classes.fieldRow}>
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="type-input" shrink={true}>{translate.t('laType')}</InputLabel>
                <Field
                  id="type"
                  type="select"
                  component={Select}
                  name="type"
                  value={values.type}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  error={Boolean(errors.type && touched.type)}
                  inputProps={{ name: 'type', id: 'type-input' }}
                  disabled={isUsed}
                >
                  {taskTypes.map((task: any) => (
                    <MenuItem key={task.code} value={task.code}>
                      {task.name}
                    </MenuItem>
                  ))}
                </Field>
                {(errors.type && touched.type) && (
                  <FormHelperText>{errors.type}</FormHelperText>
                )}
              </FormControl>
              <FormControl fullWidth margin="normal">
                <InputLabel htmlFor="language-input" shrink={true}>{translate.t('laDefaultTemplateLanguage')}</InputLabel>
                <Field
                  id="language"
                  type="select"
                  component={Select}
                  name="language"
                  value={values.language || defaultLanguage.fComLanguageCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  style={{ width: '200px' }}
                  error={Boolean(errors.language && touched.language)}
                  inputProps={{ name: 'language', id: 'language-input' }}
                  disabled={isUsed}
                >
                  {languages.map((lang: any) => (
                    <MenuItem key={lang.fComLanguageCode} value={lang.fComLanguageCode}>
                      {lang.fComLanguageName}
                    </MenuItem>
                  ))}
                </Field>
              </FormControl>
            </div>
            <Field
              id="title"
              autoFocus
              required={true}
              component={TextField}
              error={Boolean(errors.title && touched.title)}
              fullWidth
              helperText={(errors.title && touched.title) && errors.title}
              label={translate.t('laTitle')}
              margin="normal"
              name="title"
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.title}
              disabled={isUsed}
            />
            <Field
              id="description"
              component={TextField}
              error={Boolean(errors.description && touched.description)}
              fullWidth
              helperText={(errors.description && touched.description) && errors.description}
              label={translate.t('laDescription')}
              margin="normal"
              name="description"
              rows={2}
              rowsMax={5}
              multiline={true}
              onBlur={handleBlur}
              onChange={handleChange}
              value={values.description}
              disabled={isUsed}
            />
            <Paper className={classes.paper}>
              <Field
                  name="automationEnabled"
                  component={FormikCheckboxField}
                  onChange={handleChange}
                  checked={values.automationEnabled}
                  label={translate.t('laAutomationEnabled')}
              />

              <Field
                  name="automationUnits"
                  label={translate.t('label_audience_for_units')}
                  component={FormikSelectField}
                  onChange={handleAutomationUnitChange}
                  options={allUnitOptions}
                  isClearable={true}
                  isMulti
                  value={values.automationUnits}
              />
              <Button onClick={handleIncludeAllSubUnits} disabled={isAllSubUnitsIncluded} color={'primary'}>
                <Add /> {translate.t('laIncludeSubunits')}
              </Button>

              <div className={classes.fieldRow}>
                <FormControl fullWidth margin="normal">
                  <InputLabel htmlFor="automationTriggerCondition-input" shrink={true} required={values.automationEnabled}>{translate.t('laTriggerCondition')}</InputLabel>
                  <Field
                      id="automationTriggerCondition"
                      type="select"
                      component={Select}
                      name="automationTriggerCondition"
                      value={values.automationTriggerCondition || ''}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      error={Boolean(errors.automationTriggerCondition && touched.automationTriggerCondition)}
                      inputProps={{ name: 'automationTriggerCondition', id: 'automationTriggerCondition-input' }}
                      required={values.automationEnabled}
                  >
                    {options.triggerFieldOptions.map(({value, label}) => (
                        <MenuItem key={value} value={value}>
                          {label}
                        </MenuItem>
                    ))}
                  </Field>
                </FormControl>

                <Field
                    id="automationTriggerOffset"
                    component={TextField}
                    type={'number'}
                    error={Boolean(errors.automationTriggerOffset && touched.automationTriggerOffset)}
                    fullWidth
                    helperText={(errors.automationTriggerOffset && touched.automationTriggerOffset) && errors.automationTriggerOffset}
                    label={translate.t('laTriggerOffset')}
                    margin="normal"
                    name="automationTriggerOffset"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.automationTriggerOffset}
                    required={values.automationEnabled}
                />
              </div>
            </Paper>
          </Form>
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            disabled={isSubmitting}
            onClick={handleCancel}
          >
            {translate.t('laCancel')}
          </Button>
          <Button
            color="primary"
            variant="contained"
            disabled={isSubmitting || !isValid}
            onClick={handleSubmitClick}
          >
            {submitContent}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSave}
      validationSchema={validationSchema}
    >
      {renderDialog}
    </Formik>
  );
};

const enhance = compose<Props, OwnProps>(
  withStyles(styles, { withTheme: true })
);

export default enhance(WorklistEditDialog);
